import classnames from 'classnames';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Image } from 'components/Image/Image';
import Label from 'components/form/Label/Label';
import {
  formatAsFull,
  formatAsFullWithCompleteMonth,
  formatWithTimeOnly,
  getSortableDate,
  parseSortableDate,
} from 'helpers/dateHelper';
import { formatUSDwithSign } from 'helpers/money.helper';
import './TransactionViewRedesign.scss';

const convertToDateMap = recentTransactions => {
  const transactionMap = {};
  const orderedKeys = [];

  recentTransactions?.forEach(transaction => {
    let timestamp;
    if (transaction?.transaction) {
      timestamp = transaction?.transaction?.transactionDate;
    } else if (transaction?.bankTransfer) {
      timestamp = transaction?.bankTransfer.transferDate;
    } else if (transaction?.date) {
      timestamp = transaction?.date;
    }
    const dateKey = getSortableDate(timestamp);

    if (!transactionMap[dateKey]) {
      transactionMap[dateKey] = [];
      orderedKeys.push(dateKey);
    }

    transactionMap[dateKey]?.push(transaction);
  });

  orderedKeys.sort((a, b) => Number(b) - Number(a));

  return {
    orderedKeys,
    transactionMap,
  };
};

const Section = props => {
  return <>{props.children}</>;
};

const BlockLabel = props => {
  return (
    <Label className="toUpper format100 padTB2 padLR16 bgGray025 text-14">
      {props.children}
    </Label>
  );
};

const Conditional = props => {
  if (props.condition) return props.children;
  else return '';
};

const TransactionRow = props => {
  return (
    <FlexContainer
      justifyBetween
      className={classnames('padTB10 format100', {
        'borderColorGray025 borderBottom': !props.isLast,
        'm-padLR16': props.isDetailsView,
        pointer: props.triggerOnClick,
      })}
      onClick={e => {
        if (props.triggerOnClick) props.onClickTransactionRow(props.metadata);
      }}
    >
      <FlexContainer
        column
        className={classnames({
          padL20: !props.isDetailsView,
          'm-format40 format70': props.quantityOfTags === 2,
          'm-format70 format85': props.quantityOfTags < 2,
        })}
      >
        <Label className="text-16 m-text-14 m-text-12 bold overflow-hidden ellipsis whitespace-nowrap format100">
          {props.title}
        </Label>
        {!props.hideTimestamp && (
          <Label className="text-14 m-text-12 padT4">{props.timestamp}</Label>
        )}
      </FlexContainer>
      <FlexContainer
        column
        className={classnames('justify-start align-items-end', {
          padR20: !props.isDetailsView,
          'm-format60 format30': props.quantityOfTags === 2,
          'm-format30 format15': props.quantityOfTags < 2,
        })}
      >
        <Label
          className={classnames(
            'text-right',
            'text-16 m-text-14',
            'noWrapText',
            {
              'fontGreen400 bold': props.amount > 0,
            },
          )}
        >
          {formatUSDwithSign(props.amount)}
        </Label>
        <Label className="text-right text-14 m-text-12 fontGray300 italic padT4 whitespace-nowrap">
          {props.description}
        </Label>
      </FlexContainer>
    </FlexContainer>
  );
};

const TagLabel = props => {
  const styleTag = classnames(
    'borderRadius16 text-12 m-text-10 bold normal padT2 padB1 padLR8',
    props.className,
  );
  return (
    <FlexContainer justifyCenter alignItemsCenter className={styleTag}>
      {props.label}
    </FlexContainer>
  );
};

const TagLabels = props => {
  const { tags } = props || {};
  return (
    <FlexContainer justifyBetween alignItemsCenter>
      {tags?.map((data, index) => {
        return (
          <TagLabel
            key={index}
            className={data?.className}
            label={data?.label}
          />
        );
      })}
    </FlexContainer>
  );
};

const TransactionPropertyMapper = props => {
  const metadata = props.transfer;
  const formatDate = props.showBlockLabel ? formatWithTimeOnly : formatAsFull;

  if (metadata?.bankTransfer) {
    const sign = metadata?.bankTransfer.type === 'C2B' ? -1 : +1;
    return (
      <TransactionRow
        title={metadata?.bankTransfer.accountName}
        timestamp={formatDate(metadata?.bankTransfer.transferDate)}
        amount={sign * metadata?.bankTransfer.amount}
        description={
          metadata?.bankTransfer.status === 'PENDING' ? 'Pending' : null
        }
        isLast={!props.isDetailsView && props.isLast}
        isDetailsView={props.isDetailsView}
        hideTimestamp={false}
      />
    );
  } else if (metadata?.transaction) {
    const sign =
      metadata?.transaction?.transactionDirection === 'DEBIT' ? -1 : +1;

    const tags = [];
    if (
      metadata?.buttonOfferRedemption &&
      metadata?.transaction?.transactionType !==
        'BUTTON_OFFER_REDEMPTION_CREDIT'
    ) {
      tags.push({
        className: 'bgGreenG025 fontGreenG400',
        label: (
          <FlexContainer row alignItemsCenter justifyCenter>
            CASH BACK
            <Image imgClass="chevronRightGreen" />
          </FlexContainer>
        ),
      });
    }

    if (
      metadata?.securedCard?.transferred ||
      metadata?.transaction?.transactionType ===
        'SECURED_CARD_TRANSACTION_TRANSFER'
    ) {
      tags.push({
        className: 'margL10 bgBlueB025 fontBlueB400',
        label: 'CREDIT BUILDER',
      });
    }

    let title =
      metadata?.transaction?.merchantName ??
      metadata?.transaction?.transactorName;

    const description = <TagLabels tags={tags} />;
    if (
      metadata?.buttonOfferRedemption?.merchantName &&
      metadata?.transaction?.transactionType ===
        'BUTTON_OFFER_REDEMPTION_CREDIT'
    ) {
      title = `Cash back at ${metadata?.buttonOfferRedemption?.merchantName}`;
    }
    return (
      <TransactionRow
        title={title}
        timestamp={formatDate(metadata?.transaction?.transactionDate)}
        amount={sign * metadata?.transaction?.transactionAmount}
        isLast={!props.isDetailsView && props.isLast}
        isDetailsView={props.isDetailsView}
        quantityOfTags={tags?.length}
        onClickTransactionRow={props.onClickTransactionRow}
        metadata={metadata}
        description={description}
        triggerOnClick={
          metadata?.buttonOfferRedemption &&
          metadata?.transaction?.transactionType !==
            'BUTTON_OFFER_REDEMPTION_CREDIT' &&
          props.isDetailsView
        }
      />
    );
  } else {
    return (
      <TransactionRow
        title={metadata?.description}
        timestamp={formatAsFull(metadata?.date)}
        amount={metadata?.amount}
        isLast={props.isLast}
        isDetailsView={props.isDetailsView}
        metadata={metadata}
      />
    );
  }
};
const TransactionSection = props => {
  return (
    <Section>
      {props.showBlockLabel && (
        <BlockLabel>
          {formatAsFullWithCompleteMonth(parseSortableDate(props.date))}
        </BlockLabel>
      )}
      {props.transactions.map((t, k) => (
        <TransactionPropertyMapper
          transfer={t}
          isLast={k === props.transactions.length - 1}
          isDetailsView={props.isDetailsView}
          onClickTransactionRow={props.onClickTransactionRow}
          showBlockLabel={props.showBlockLabel}
        />
      ))}
    </Section>
  );
};

const convertBankTransfer = bts => {
  return bts
    ?.filter(
      bt =>
        bt.status !== 'PENDING' &&
        bt.status !== 'FAILED' &&
        bt.status !== 'COMPLETED',
    )
    .map(bt => ({ bankTransfer: bt }));
};

const extractTimestampFromEntry = transaction => {
  if (transaction?.transaction) {
    return transaction?.transaction?.transactionDate;
  } else if (transaction?.bankTransfer) {
    return transaction?.bankTransfer?.transferDate;
  }
};

const EmptyTransactions = props => {
  const emptyStyles =
    'borderRadius4 borderDashed borderColorGrayN300 padTB30 m-padLR50 margTB20 margLR16 m-text-center m-text-14';
  return (
    <FlexContainer justifyCenter alignItemsCenter className={emptyStyles}>
      You haven’t made any recent transactions
    </FlexContainer>
  );
};

const TransactionViewRedesign = props => {
  const fullTransactionList = [];
  fullTransactionList.push(...props.transactions);
  fullTransactionList.push(...convertBankTransfer(props.bankTransfers));
  fullTransactionList.sort((a, b) => {
    const aTs = getSortableDate(extractTimestampFromEntry(a));
    const bTs = getSortableDate(extractTimestampFromEntry(b));

    return Number(bTs) - Number(aTs);
  });
  const pendingBankTransfers = props.bankTransfers
    ?.filter(bt => bt.status === 'PENDING')
    .map(bt => ({ bankTransfer: bt }));

  const diff = props.limit - pendingBankTransfers?.length;
  const transactionsToShow = diff > 0 ? diff : 0;
  if (transactionsToShow < fullTransactionList?.length) {
    fullTransactionList.splice(transactionsToShow);
  }

  const processedTransactions = convertToDateMap(fullTransactionList);

  if (
    pendingBankTransfers?.length === 0 &&
    processedTransactions?.orderedKeys?.length === 0
  ) {
    return <EmptyTransactions isDetailsView={props.isDetailsView} />;
  }

  return (
    <div className="TransactionViewRedesign">
      <Conditional condition={pendingBankTransfers?.length > 0}>
        <Section>
          {props.showBlockLabel && <BlockLabel>Pending</BlockLabel>}
          {pendingBankTransfers?.map((t, k) => (
            <TransactionPropertyMapper
              key={k}
              transfer={t}
              isLast={
                k === pendingBankTransfers.length - 1 &&
                processedTransactions?.orderedKeys?.length === 0
              }
              showBlockLabel={false}
              isDetailsView={props.isDetailsView}
            />
          ))}
        </Section>
      </Conditional>
      {processedTransactions?.orderedKeys?.map((d, k) => (
        <TransactionSection
          key={k}
          date={d}
          transactions={processedTransactions?.transactionMap?.[d]}
          isDetailsView={props.isDetailsView}
          onClickTransactionRow={props.onClickTransactionRow}
          showBlockLabel={props.showBlockLabel}
        />
      ))}
    </div>
  );
};

TransactionViewRedesign.BlockLabel = BlockLabel;

TransactionViewRedesign.defaultProps = {
  limit: Number.MAX_SAFE_INTEGER,
  showBlockLabel: true,
};

export default TransactionViewRedesign;
